import React, { useState, useEffect } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Transaction, TransactionCategory, FinancialAccount } from '../../types/financial';
import './Financial.css';

interface TransactionModalProps {
  isOpen: boolean;
  onClose: () => void;
  transaction: Transaction | null;
  categories: TransactionCategory[];
  accounts: FinancialAccount[];
  onSave: (data: Omit<Transaction, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
}

export const TransactionModal: React.FC<TransactionModalProps> = ({
  isOpen,
  onClose,
  transaction,
  categories,
  accounts,
  onSave,
}) => {
  const [type, setType] = useState<'receita' | 'despesa'>('receita');
  const [categoryId, setCategoryId] = useState('');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [accountId, setAccountId] = useState('');
  const [status, setStatus] = useState<'pendente' | 'pago' | 'cancelado'>('pendente');
  const [paymentDate, setPaymentDate] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (transaction) {
      setType(transaction.type);
      setCategoryId(transaction.categoryId);
      setAmount(transaction.amount.toFixed(2).replace('.', ','));
      setDescription(transaction.description);
      setDate(transaction.date.split('T')[0]);
      setAccountId(transaction.accountId || '');
      setStatus(transaction.status);
      setPaymentDate(transaction.paymentDate ? transaction.paymentDate.split('T')[0] : '');
      setNotes(transaction.notes || '');
    } else {
      setType('receita');
      setCategoryId('');
      setAmount('');
      setDescription('');
      setDate(new Date().toISOString().split('T')[0]);
      setAccountId('');
      setStatus('pendente');
      setPaymentDate('');
      setNotes('');
    }
  }, [transaction, isOpen]);

  const filteredCategories = categories.filter(c => c.type === type);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!categoryId) {
      alert('Selecione uma categoria');
      return;
    }

    const category = categories.find(c => c.id === categoryId);
    if (!category) {
      alert('Categoria não encontrada');
      return;
    }

    const account = accountId ? accounts.find(a => a.id === accountId) : undefined;

    const amountNum = parseFloat(amount.replace(',', '.')) || 0;
    if (amountNum <= 0) {
      alert('O valor deve ser maior que zero');
      return;
    }

    setLoading(true);
    try {
      await onSave({
        type,
        categoryId,
        categoryName: category.name,
        amount: amountNum,
        description,
        date: new Date(date).toISOString(),
        accountId: accountId || undefined,
        accountName: account?.name,
        status,
        paymentDate: status === 'pago' && paymentDate ? new Date(paymentDate).toISOString() : undefined,
        notes: notes || undefined,
        relatedConsultationId: transaction?.relatedConsultationId,
      });
      onClose();
    } catch (error: any) {
      console.error('Erro ao salvar transação:', error);
      alert(error.message || 'Erro ao salvar transação');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={transaction ? 'Editar Transação' : 'Nova Transação'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? 'Salvando...' : 'Salvar'}
          </Button>
        </>
      }
    >
      <form className="ns-form" onSubmit={handleSubmit}>
        <div className="ns-form-row">
          <label className="ns-field">
            <span>Tipo</span>
            <select
              className="ns-input"
              value={type}
              onChange={(e) => {
                setType(e.target.value as 'receita' | 'despesa');
                setCategoryId(''); // Reset category when type changes
              }}
              required
            >
              <option value="receita">Receita</option>
              <option value="despesa">Despesa</option>
            </select>
          </label>

          <label className="ns-field">
            <span>Categoria</span>
            <select
              className="ns-input"
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              required
            >
              <option value="">Selecione uma categoria</option>
              {filteredCategories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="ns-form-row">
          <label className="ns-field">
            <span>Valor (R$)</span>
            <input
              className="ns-input"
              type="text"
              value={amount}
              onChange={(e) => {
                const value = e.target.value.replace(/[^\d,.-]/g, '');
                setAmount(value);
              }}
              placeholder="0,00"
              required
            />
          </label>

          <label className="ns-field">
            <span>Data</span>
            <input
              className="ns-input"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
            />
          </label>
        </div>

        <label className="ns-field">
          <span>Descrição</span>
          <input
            className="ns-input"
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Descreva a transação"
            required
          />
        </label>

        <div className="ns-form-row">
          <label className="ns-field">
            <span>Conta (opcional)</span>
            <select
              className="ns-input"
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
            >
              <option value="">Nenhuma conta</option>
              {accounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.name}
                </option>
              ))}
            </select>
          </label>

          <label className="ns-field">
            <span>Status</span>
            <select
              className="ns-input"
              value={status}
              onChange={(e) => setStatus(e.target.value as 'pendente' | 'pago' | 'cancelado')}
            >
              <option value="pendente">Pendente</option>
              <option value="pago">Pago</option>
              <option value="cancelado">Cancelado</option>
            </select>
          </label>
        </div>

        {status === 'pago' && (
          <label className="ns-field">
            <span>Data de Pagamento</span>
            <input
              className="ns-input"
              type="date"
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
            />
          </label>
        )}

        <label className="ns-field">
          <span>Observações (opcional)</span>
          <textarea
            className="ns-input"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Adicione observações sobre esta transação"
            rows={3}
          />
        </label>
      </form>
    </Modal>
  );
};







